import { motion } from 'framer-motion';
import { Check, Leaf, Sprout, TreePalm } from 'lucide-react';
import { MetaTags } from '../components/seo/MetaTags';
import { useAuth } from '../hooks/use-auth';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getUserData } from '../lib/firestore';
import { LoadingSpinner } from '../components/loading/LoadingSpinner';
import { FAQ } from '../components/faq/FAQ';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '0',
    description: 'Perfect for getting started',
    icon: Sprout,
    features: [
      '5 Credits per month',
      'Basic plant care guides',
      'Community access',
      'Email support'
    ],
    color: 'emerald',
    popular: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '999',
    description: 'Best for plant enthusiasts',
    icon: Leaf,
    features: [
      '75 Credits per month',
      'Detailed care instructions',
      'Disease detection',
      'Priority support',
      'Plant collection manager',
      'Custom care reminders'
    ],
    color: 'emerald',
    popular: true
  },
  {
    id: 'business',
    name: 'Business',
    price: '2,999',
    description: 'For professional gardeners',
    icon: TreePalm,
    features: [
      'Everything in Pro',
      'API access',
      'Team collaboration',
      'Advanced analytics',
      'White-label option',
      'Dedicated account manager',
      'Custom integrations'
    ],
    color: 'emerald',
    popular: false
  }
];

// Pricing-specific FAQs
const pricingFAQs = [
  {
    category: 'Plans & Pricing',
    questions: [
      {
        q: "What's included in the free plan?",
        a: 'The free plan includes 5 plant identifications per month, basic plant care guides, community access, and email support. Perfect for casual users and hobbyists.'
      },
      {
        q: 'Can I upgrade or downgrade my plan at any time?',
        a: "Yes! You can change your plan at any time. When upgrading, you'll be charged the prorated amount for the remainder of the billing cycle. When downgrading, the change will take effect at the start of your next billing cycle."
      },
      {
        q: 'Do unused credits roll over to the next month?',
        a: 'No, credits reset at the beginning of each month for all plans. This helps us maintain fair usage and provide the best service to all users.'
      },
      {
        q: 'Is there a long-term commitment?',
        a: 'No, all our plans are month-to-month with no long-term commitment required. You can cancel at any time.'
      }
    ]
  },
  {
    category: 'Billing & Payment',
    questions: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards, debit cards, and popular digital payment methods. All payments are processed securely through our payment provider.'
      },
      {
        q: 'Is there a refund policy?',
        a: "Yes, we offer a 7-day money-back guarantee for all paid plans. If you're not satisfied, contact our support team within 7 days of your purchase for a full refund."
      },
      {
        q: 'Are there any hidden fees?',
        a: 'No hidden fees! The price you see is the price you pay. All prices are inclusive of applicable taxes and fees.'
      }
    ]
  }
  // {
  //   category: 'Business Plans',
  //   questions: [
  //     {
  //       q: 'Can I add team members to my business plan?',
  //       a: 'Yes! The business plan includes team collaboration features. You can add team members and manage their access levels from your dashboard.'
  //     },
  //     {
  //       q: 'What kind of API access is included?',
  //       a: 'Business plans include full API access with comprehensive documentation, allowing you to integrate our plant identification service into your own applications.'
  //     },
  //     {
  //       q: 'Do you offer custom enterprise solutions?',
  //       a: 'Yes, for larger organizations we offer custom enterprise solutions with dedicated support, custom integrations, and flexible billing options. Contact our sales team for details.'
  //     }
  //   ]
  // }
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchUserData() {
      if (user) {
        setIsLoading(true);
        try {
          const data = await getUserData(user.uid);
          setUserData(data);
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    }

    fetchUserData();
  }, [user]);

  const handlePlanClick = planId => {
    if (user) {
      navigate('/dashboard');
      return;
    } else {
      navigate('/signup');
      return;
    }
  };

  return (
    <>
      <MetaTags
        title="Pricing - Plant Identifier"
        description="Choose the perfect plan for your plant identification needs. From hobbyist to professional, we have you covered."
      />
      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 to-transparent pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the perfect plan for your plant identification needs
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4"
          >
            {plans.map(plan => {
              const isCurrentPlan =
                userData?.userTier?.toLowerCase() === plan.id;

              return (
                <motion.div
                  key={plan.id}
                  variants={cardVariants}
                  whileHover={{ scale: 1.02 }}
                  className={`
                    relative bg-white rounded-2xl shadow-xl overflow-hidden
                    ${plan.popular ? 'ring-2 ring-emerald-500 scale-105 md:scale-110' : ''}
                  `}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white px-4 py-1 rounded-bl-lg text-sm font-medium">
                      Most Popular
                    </div>
                  )}

                  {isCurrentPlan && (
                    <div className="absolute top-0 left-0 bg-emerald-500 text-white px-4 py-1 rounded-br-lg text-sm font-medium">
                      Current Plan
                    </div>
                  )}

                  <div className="p-8">
                    {/* Plan Header */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 rounded-xl bg-emerald-100">
                        <plan.icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {plan.name}
                        </h3>
                        <p className="text-gray-500">{plan.description}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-gray-900">
                          ₹{plan.price}
                        </span>
                        <span className="text-gray-500 ml-2">/month</span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    {isLoading ? (
                      <div className="flex justify-center py-3">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handlePlanClick(plan.id)}
                        className={`
                          w-full py-3 rounded-xl font-medium transition-colors
                          ${
                            isCurrentPlan
                              ? 'bg-gray-100 text-gray-400 cursor-default'
                              : plan.popular
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                          }
                        `}
                        // disabled={!isCurrentPlan}
                      >
                        {isCurrentPlan
                          ? 'Current Plan'
                          : user
                            ? plan.price === '0'
                              ? 'Explore Free Plan'
                              : `Upcoming ${plan.name} Plan`
                            : 'Get Started'}
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-7xl mx-auto mt-24 text-center"
          >
            <FAQ faqs={pricingFAQs} title="Pricing FAQ" />
            <p className="text-gray-600">
              Have more questions?{' '}
              <Link
                to="/help"
                className="text-emerald-600 hover:text-emerald-700"
              >
                Contact our support team
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
}
